import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { com } from '@eidu/web'
import useResource from '../../../../../io/useResource'
import MergeEditor from '../../../../../components/data/MergeEditor'
import DiffStats from './DiffStats'
import EntityDiffSummary from './EntityDiffSummary'
import { EntitySnapshotDiff } from './entitySnapshotDiff'
import EntityType = com.eidu.sharedlib.entity.type.EntityType

type ResourceDiffDialogProps = {
  onClose: () => void
  leftKey: string | undefined
  rightKey: string | undefined
  leftLabel: string
  rightLabel: string
  title?: string
  entityDiff?: EntitySnapshotDiff
  entityType?: EntityType
}

const ResourceDiffDialog = ({
  onClose,
  leftKey,
  rightKey,
  leftLabel,
  rightLabel,
  title,
  entityDiff,
  entityType,
}: ResourceDiffDialogProps) => {
  const showPayloadDiff = !!leftKey && !!rightKey
  const {
    content: leftContent,
    isLoading: leftLoading,
    error: leftError,
  } = useResource(showPayloadDiff ? leftKey : undefined)
  const {
    content: rightContent,
    isLoading: rightLoading,
    error: rightError,
  } = useResource(showPayloadDiff ? rightKey : undefined)

  const loading = showPayloadDiff && (leftLoading || rightLoading)
  const error = leftError || rightError
  const ready = showPayloadDiff && !loading && !error && leftContent !== undefined && rightContent !== undefined

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{ sx: { height: '85vh' } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Stack>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
            Compare
          </Typography>
          <Typography variant="h6">{title ?? 'Diff'}</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 0.5, color: 'text.secondary', alignItems: 'center' }}>
            <Typography variant="caption">← {leftLabel}</Typography>
            <Typography variant="caption">→ {rightLabel}</Typography>
            {ready && <DiffStats left={leftContent} right={rightContent} />}
          </Stack>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="Close diff"
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {entityDiff && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Fields & links
            </Typography>
            <EntityDiffSummary
              diff={entityDiff}
              entityType={entityType}
              beforeLabel={leftLabel}
              afterLabel={rightLabel}
            />
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            Payload
          </Typography>
          {showPayloadDiff ? (
            <>
              {loading && (
                <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Stack>
              )}
              {error && (
                <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', px: 3 }}>
                  <Typography color="error">Failed to load diff: {error.message}</Typography>
                </Stack>
              )}
              {ready && <MergeEditor left={leftContent} right={rightContent} />}
            </>
          ) : (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Payload unchanged.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ResourceDiffDialog
